import api from './api';
import { TipoUsuario } from '../types/enums/TipoUsuario';
import { TenantInfo } from '../types/models';

class AuthService {
  // Authentication methods
  async signIn(email: string, senha: string) {
    const response = await api.post('/api/auth/signin', { email, senha });
    const { token, guid, name, roles, subscription, error, role, needsPayment, taxId, cellphone, tenants, admin, tenantId, tenantName } = response.data;

    if (error) {
      throw new Error(error);
    }

    if (!token) {
      throw new Error('Token não encontrado na resposta');
    }

    // Remover o prefixo "Bearer: " se vier do backend
    const cleanToken = token.replace(/^Bearer:\s*/i, '');

    localStorage.setItem('@ThinkWay:token', cleanToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;

    // Se tem múltiplos tenants, sinalizar para seleção
    const needsSelectTenant = tenants && tenants.length > 1;

    return {
      user: { guid, name, email, role, roles, taxId, cellphone, admin },
      subscription: subscription || null,
      needsPayment: needsPayment === true,
      tenants: tenants || [],
      needsSelectTenant,
      tenantId,
      tenantName
    };
  }
  
  async signUp(nome: string, email: string, senha: string, tipoUsuario: TipoUsuario, taxId: string, cellphone?: string) {
    try {
      const response = await api.post('/api/auth/signup', { nome, email, senha, tipoUsuario, taxId, cellphone });
      const { token, guid, name, role, roles, needsPayment, taxId: responseTaxId, cellphone: responseCellphone } = response.data;
      
      if (!token) {
        throw new Error('Token não encontrado na resposta');
      }
      
      // Remover o prefixo "Bearer: " se vier do backend
      const cleanToken = token.replace(/^Bearer:\s*/i, '');
      
      // Salvar token e configurar autenticação
      localStorage.setItem('@ThinkWay:token', cleanToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
      
      return {
        success: true,
        user: {
          guid,
          name: name || nome,
          email,
          role,
          roles: roles || [],
          tipoUsuario,
          taxId: responseTaxId || taxId,
          cellphone: responseCellphone || cellphone
        },
        needsPayment: needsPayment === true // Respeitar o valor do backend: false = acesso grátis, true = precisa pagar
      };
    } catch (error: any) {
      // Extrair mensagem de erro da resposta da API
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao realizar cadastro';
      throw new Error(errorMessage);
    }
  }
  
  async renewToken() {
    const response = await api.post('/api/auth/renew-token');
    
    // Update token if a new one was provided
    if (response.data.token) {
      // Remover o prefixo "Bearer: " se vier do backend
      const cleanToken = response.data.token.replace(/^Bearer:\s*/i, '');
      localStorage.setItem('@ThinkWay:token', cleanToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
    }
    
    return response.data;
  }
  
  async selectTenant(tenantId: string) {
    const response = await api.post('/api/auth/select-tenant', { tenantId });
    const data = response.data;

    if (data.token) {
      const cleanToken = data.token.replace(/^Bearer:\s*/i, '');
      localStorage.setItem('@ThinkWay:token', cleanToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
    }

    return data;
  }

  async getMyTenants(): Promise<TenantInfo[]> {
    const response = await api.get('/api/auth/my-tenants');
    return response.data;
  }

  signOut() {
    localStorage.removeItem('@ThinkWay:token');
    api.defaults.headers.common['Authorization'] = '';
  }
  
  // User management methods
  async updateUserName(newName: string) {
    const response = await api.put(`/api/users/name`, { newName });
    return response.data;
  }
  
  async updateUserPassword(currentPassword: string, newPassword: string) {
    const response = await api.put(`/api/users/password`, {
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async requestPasswordReset(email: string) {
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao solicitar redefinição de senha';
      throw new Error(errorMessage);
    }
  }

  async validatePasswordResetToken(token: string) {
    try {
      const response = await api.get('/api/auth/reset-password/validate', {
        params: { token }
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Token inválido ou expirado';
      throw new Error(errorMessage);
    }
  }

  async resetPassword(token: string, newPassword: string) {
    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        newPassword
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao redefinir senha';
      throw new Error(errorMessage);
    }
  }
  
  // Token management
  getStoredToken() {
    return localStorage.getItem('@ThinkWay:token');
  }
  
  setAuthHeader(token: string) {
    // Remover o prefixo "Bearer" ou "Bearer: " se já existir
    const cleanToken = token.replace(/^Bearer:\s*/i, '').replace(/^Bearer\s+/i, '');
    api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
  }
}

export default new AuthService();
